import { buildPreviewSafeAreaOverlay, formatPreviewSafeAreaLabel, formatPreviewSafeAreaTooltip } from 'common/preview-safe-area'

export default function playerPreviewComponent ($log, $timeout, ApiService, PlayerFactory, PlayerUtil, SceneService, CONFIGURATION) {
  'ngInject'
  return {
    restrict: 'E',
    scope: {
      scene: '='
    },
    bindToController: {},
    controller: () => {},
    controllerAs: 'ctrl',
    template: require('./player-preview.component.html').default,
    link: function (scope, elem) {
      const previewSafeAreaConfig = (CONFIGURATION && CONFIGURATION.previewSafeArea) || {}

      // Setup stage
      const stageWrapper = angular.element(elem[0].querySelector('#stageWrapper'))
      const stage = angular.element(elem[0].querySelector('#stage'))

      function setStageOrientation (orientation) {
        stageWrapper.removeClass('landscape')
        stageWrapper.removeClass('portrait')
        PlayerUtil.setOrientationClass(orientation, stageWrapper)
      }

      setStageOrientation(scope.scene.orientation)

      // RF2026: Persistent animated background overlay (lives in stageWrapper, not stage,
      // so stage.html('') never removes it and the animation runs uninterrupted)
      const rf2026Bg = angular.element(
        '<div id="rf2026-bg">' +
          '<div class="rf2026-gradient"></div>' +
          '<div class="rf2026-blur"></div>' +
        '</div>'
      )
      stageWrapper.append(rf2026Bg)

      const safeAreaOverlay = angular.element(
        '<div class="preview-safe-area preview-safe-area--stage" style="display: none;">' +
          '<div class="preview-safe-area__bar preview-safe-area__bar--left" style="display: none;"></div>' +
          '<div class="preview-safe-area__bar preview-safe-area__bar--right" style="display: none;"></div>' +
          '<div class="preview-safe-area__box"></div>' +
          '<div class="preview-safe-area__label"></div>' +
        '</div>'
      )
      const safeAreaBox = angular.element(safeAreaOverlay[0].querySelector('.preview-safe-area__box'))
      const safeAreaLabel = angular.element(safeAreaOverlay[0].querySelector('.preview-safe-area__label'))
      const safeAreaLeftBar = angular.element(safeAreaOverlay[0].querySelector('.preview-safe-area__bar--left'))
      const safeAreaRightBar = angular.element(safeAreaOverlay[0].querySelector('.preview-safe-area__bar--right'))
      stageWrapper.append(safeAreaOverlay)

      function syncSafeAreaOverlay () {
        const wrapperRect = stageWrapper[0].getBoundingClientRect()

        // The safe area must track the actual rendered media, not the #stage div.
        // For portrait scenes the stage stretches to the full container width while the
        // video is centered inside it, so measuring the stage would make the overlay
        // depend on the window size. Measuring the media element keeps it stable and
        // mirrors how the modal preview positions the overlay over its video frame.
        const rootEl = stage[0].querySelector('[data-safe-area-root]') || stage[0].querySelector('video, img')
        if (!rootEl) {
          safeAreaOverlay.css('display', 'none')
          return
        }
        const rootRect = rootEl.getBoundingClientRect()
        const targetRect = (rootRect.width && rootRect.height)
          ? rootRect
          : stage[0].getBoundingClientRect()

        if (!targetRect.width || !targetRect.height) {
          safeAreaOverlay.css('display', 'none')
          return
        }

        const overlay = buildPreviewSafeAreaOverlay(
          previewSafeAreaConfig,
          scope.scene && scope.scene.orientation,
          targetRect.width,
          targetRect.height
        )

        if (!overlay) {
          safeAreaOverlay.css('display', 'none')
          return
        }

        // For pillarbox content the screen is wider than the video, so the safe-area
        // frame must extend beyond the media on both sides. The stage background is
        // black, so the exposed area between the video and the frame reads as the
        // real screen's black side bars. Crop content keeps the media-sized frame.
        let overlayWidth = targetRect.width
        let overlayLeft = targetRect.left - wrapperRect.left

        if (overlay.mode === 'pillarbox' && overlay.videoWidthRatio > 0) {
          overlayWidth = targetRect.width / overlay.videoWidthRatio
          overlayLeft -= (overlayWidth - targetRect.width) / 2
        }

        safeAreaOverlay.css({
          display: 'block',
          left: `${overlayLeft}px`,
          top: `${targetRect.top - wrapperRect.top}px`,
          width: `${overlayWidth}px`,
          height: `${targetRect.height}px`,
          right: 'auto',
          bottom: 'auto'
        })
        safeAreaBox.css(overlay.boxStyle)
        safeAreaLabel.text(formatPreviewSafeAreaLabel(overlay))
        safeAreaLabel.attr('title', formatPreviewSafeAreaTooltip(overlay))

        if (overlay.mode === 'pillarbox') {
          safeAreaLeftBar.css(Object.assign({ display: 'block' }, overlay.leftBarStyle))
          safeAreaRightBar.css(Object.assign({ display: 'block' }, overlay.rightBarStyle))
        } else {
          safeAreaLeftBar.css('display', 'none')
          safeAreaRightBar.css('display', 'none')
        }
      }

      const handleResize = syncSafeAreaOverlay
      window.addEventListener('resize', handleResize)

      const unwatchOrientation = scope.$watch(() => scope.scene && scope.scene.orientation, orientation => {
        if (!orientation) {
          return
        }

        setStageOrientation(orientation)
        $timeout(syncSafeAreaOverlay)
      })

      $timeout(syncSafeAreaOverlay)

      let currentlyPlayingId
      let playing

      // Monitor for changes to currentlyPlayingId on the scene
      const unsubscribe = SceneService.subscribeToId(scope.scene.id, scene => {
        // Only proceed if scene exists
        if (!scene) {
          $log.debug('[PlayerPreview] Scene is undefined for scene id', scope.scene.id)
          return
        }

        // On change
        if (currentlyPlayingId !== scene.currentlyPlayingId) {
          currentlyPlayingId = scene.currentlyPlayingId

          // Only proceed if we have a valid currentlyPlayingId
          if (!currentlyPlayingId) {
            $log.debug('[PlayerPreview] No currentlyPlayingId set for scene', scope.scene.id)
            return
          }

          // Fetch playlistItem
          ApiService.call('playlistitem/getPlaylistItem', { id: currentlyPlayingId })
            .then(playlistItem => {
              if (typeof playlistItem.itemData === 'string') {
                try {
                  // Parse
                  playlistItem.itemData = JSON.parse(playlistItem.itemData)

                  // Do the shuffle
                  const itemData = playlistItem.itemData
                  delete playlistItem.itemData
                  Object.assign(playlistItem, itemData)
                } catch (e) {
                  $log.error('[PlayerPreview] Could not load parse itemData', e)
                  return
                }

                // Build player
                const player = PlayerFactory.create(playlistItem, scope.scene.orientation, scope)
                if (player) {
                  play(player)
                }
              }
            })
            .catch(err => {
              $log.error('[PlayerPreview] Could not load playlistItem', err)
            })
        }
      })

      scope.$on('$destroy', () => {
        unsubscribe()
        unwatchOrientation()
        window.removeEventListener('resize', handleResize)
      })

      function play (player) {
        // Play ended for previous
        if (playing) {
          playing.scope.$destroy()
          playing = null
        }

        // Play next. Get data if not provided (is on first load)
        playing = player
        stage.html('')
        stage.append(playing.elem)

        // RF2026: Toggle animated background for social/serviceAnnouncement content
        const rf2026Types = ['social', 'serviceAnnouncement']
        if (rf2026Types.includes(playing.scope.data.type)) {
          stageWrapper.addClass('rf2026-active')
        } else {
          stageWrapper.removeClass('rf2026-active')
        }

        // Re-sync once the media element knows its intrinsic dimensions, since its
        // rendered rect (and therefore the safe area geometry) is only correct then.
        const mediaEl = stage[0].querySelector('video, img')
        if (mediaEl) {
          const onMediaReady = () => $timeout(syncSafeAreaOverlay)
          mediaEl.addEventListener('loadedmetadata', onMediaReady, { once: true })
          mediaEl.addEventListener('load', onMediaReady, { once: true })
        }

        $timeout(syncSafeAreaOverlay)
      }

      // Emitted when a player is ready to play
      scope.$on('preloadComplete', function (event, id) {
        $log.log(`[PlayerPreview] preloadComplete of item ${id} for scene id ${scope.scene.id}`)
        playing.api.play()
        $timeout(syncSafeAreaOverlay)
      })

      scope.$on('playingEnded', function (event, id) {
        $log.log(`[PlayerPreview] playingEnded of item ${id} for scene id ${scope.scene.id}`)
      })

      // Emitted when a player encountered an error
      scope.$on('playerError', function (event, id, error) {
        $log.error(`[PlayerPreview] playerError of item ${id} for scene id ${scope.scene.id}. Error: ${error.message}`, error)
      })
    }
  }
}
